// frontend/src/pages/PayrollDrafts/components/DeleteConfirmModal.tsx
// Confirmation dialog for deleting a draft pay run. Rendered only when `target` is set.

import React from "react";
import { Trash2 } from "lucide-react";
import type { PayRun } from "../types";
import { fmtDate } from "../helpers";
import { C, FONT } from "../styles";

interface DeleteConfirmModalProps {
  target: PayRun;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function DeleteConfirmModal({
  target,
  deleting,
  onCancel,
  onConfirm,
}: DeleteConfirmModalProps): React.ReactElement {
  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => { if (!deleting) onCancel(); }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(16,26,43,0.42)",
          zIndex: 9998,
        }}
      />

      {/* Dialog */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: C.card,
          borderRadius: 14,
          width: "min(440px, 92vw)",
          boxShadow: "0 24px 60px rgba(16,26,43,0.28)",
          zIndex: 9999,
          fontFamily: FONT,
          padding: "24px 22px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: C.errBg,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Trash2 size={20} style={{ color: C.err }} />
          </div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.ink }}>
            Delete draft pay run?
          </h3>
        </div>

        <div style={{ fontSize: 14, color: C.ink, marginBottom: 20, lineHeight: 1.5 }}>
          This will permanently delete the draft pay run for the period{" "}
          <strong>
            {fmtDate(target.pay_period_start)} to {fmtDate(target.pay_period_end)}
          </strong>
          . This action cannot be undone.
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onCancel}
            disabled={deleting}
            style={{
              padding: "10px 16px",
              background: C.card,
              border: "1.5px solid " + C.ink,
              borderRadius: 10,
              color: C.ink,
              fontSize: 14,
              fontWeight: 700,
              cursor: deleting ? "not-allowed" : "pointer",
              fontFamily: FONT,
              opacity: deleting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            style={{
              padding: "10px 16px",
              background: C.err,
              border: "none",
              borderRadius: 10,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: deleting ? "not-allowed" : "pointer",
              fontFamily: FONT,
              opacity: deleting ? 0.5 : 1,
            }}
          >
            {deleting ? "Deleting..." : "Delete draft"}
          </button>
        </div>
      </div>
    </>
  );
}